/**
 * A rep's own colours.
 *
 * Roof Board is red, grey and navy because the company is. That is right for
 * everything a customer sees and it is nobody's favourite colour. Since
 * 2026-10-04 a rep can dress their own screen in one of a few palettes, named
 * after scents because Lysa asked for something that felt like hers rather
 * than a settings menu ([ADR 0264](../../../docs/adr/0264-a-rep-picks-their-own-colours.md)).
 *
 * Three rules:
 *
 *  1. **"Roof Board" is the default and writes nothing.** Like "match my
 *     phone" for the theme, the company look is the absence of a stamp.
 *  2. **The colours are the rep's, never the customer's.** A proposal, a
 *     contract, a storm report and a colour page are drawn in the company's
 *     palette whatever the rep who sent them has picked.
 *  3. **A scent works in light and in dark.** It sits beside the theme switch
 *     rather than replacing it: Vanilla is cream and gold by day and black and
 *     gold at night.
 *
 * The names and the list live here so the picker, the stylesheet and the
 * tests agree on them. The colours themselves are styling and live in the
 * stylesheet.
 */

export const SCENTS = [
  { id: "roofboard", name: "Roof Board", note: "The company look" },
  {
    id: "rosewater",
    name: "Rosewater",
    note: "Soft pink with a deep rose edge",
  },
  { id: "jasmine", name: "Jasmine", note: "Lavender and white petals" },
  { id: "bergamot", name: "Bergamot", note: "Warm amber on sunlit paper" },
  {
    id: "vanilla",
    name: "Vanilla",
    note: "Cream and gold; black and gold after dark",
  },
] as const;

export type ScentId = (typeof SCENTS)[number]["id"];

/** The company look. Picking it is the same as never having picked. */
export const DEFAULT_SCENT: ScentId = "roofboard";

/** The key the choice is remembered under, next to the theme's. */
export const SCENT_STORAGE_KEY = "roofboard.scent";

/** Anything unrecognised — an old value, a typo, `null` — is the company look. */
export function readScentChoice(stored: string | null | undefined): ScentId {
  return SCENTS.some((s) => s.id === stored)
    ? (stored as ScentId)
    : DEFAULT_SCENT;
}

/** The scent's name and note, for the button's label and the menu. */
export function scentById(id: ScentId): (typeof SCENTS)[number] {
  return SCENTS.find((s) => s.id === id) ?? SCENTS[0];
}
